import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import MainScreenHeader from '../../../common/Header/MainScreenHeader';
import Toolbar, { ToolbarButtonConfig, ToolbarFilterConfig } from '../../../common/Toolbar/Toolbar';
import TooltipCard from '../../../common/TooltipCard/TooltipCard';
import PopupDialog from '../../../common/PopupDialog/PopupDialog';
import ErrorMessage from '../../../common/ErrorMessage/ErrorMessage';
import './VatReturn.scss';
import { FiscalYearRestDataProvider } from '../../../../data/FiscalYearRestDataProvider';
import { VatReturnRestDataProvider } from '../../../../data/VatReturnRestDataProvider';
import { VatReturn } from '../../../../models/VatReturn';
import { FiscalYear } from '../../../../models/FiscalYear';
import { FaBook, FaCalculator, FaInfoCircle, FaBan, FaUndo } from 'react-icons/fa';
import { UserRoles } from '../../../../constants/UserRoles';

// ── Correction field map ──────────────────────────────────────────────────────
// Maps a form field key to the two VatReturn properties it touches.
const CORRECTION_FIELD_MAP: Record<string, { amountKey: keyof VatReturn; vatKey: keyof VatReturn }> = {
    '1a': { amountKey: 'highRateSuppliesAmount',          vatKey: 'highRateSuppliesVat' },
    '1b': { amountKey: 'lowRateSuppliesAmount',           vatKey: 'lowRateSuppliesVat' },
    '1c': { amountKey: 'otherRateSuppliesAmount',         vatKey: 'otherRateSuppliesVat' },
    '1d': { amountKey: 'privateUseAmount',                vatKey: 'privateUseVat' },
    '2a': { amountKey: 'reverseChargeAmount',             vatKey: 'reverseChargeVat' },
    '4a': { amountKey: 'importsOutsideEuAmount',          vatKey: 'importsOutsideEuVat' },
    '4b': { amountKey: 'intraEuAcquisitionsAmount',       vatKey: 'intraEuAcquisitionsVat' },
};

// Default tax rate (as a decimal fraction) per field — used only to pre-fill
// the UI; the user can always override.
const DEFAULT_TAX_RATE: Record<string, number> = {
    '1a': 0.21, '1b': 0.09, '1c': 0.21, '1d': 0.21,
    '2a': 0.21, '4a': 0.21, '4b': 0.21,
};


// ── Correction badge ──────────────────────────────────────────────────────────
// Small inline pill rendered next to the description of the row that carries
// the merged 1543 correction amount.
const CorrectionBadge: React.FC<{ vat: VatReturn; fmt: (v: number) => string }> = ({ vat, fmt }) => (
    <span
        className={`vat-correction-badge ${(vat.correctionVat ?? 0) > 0 ? 'vat-correction-badge--payable' : 'vat-correction-badge--receivable'}`}
        title={`BTW correctie 1543: ${fmt(Math.abs(vat.correctionVat ?? 0))}`}
    >
        1543&nbsp;{(vat.correctionVat ?? 0) > 0 ? '+' : '−'}&nbsp;{fmt(Math.abs(vat.correctionVat ?? 0))}
    </span>
);

const VatReturn: React.FC = () => {
    const { t } = useTranslation(['common', 'screens']);
    const quarters = useMemo(() => [
        { value: 'Q1', label: t('screens:vatReturn.quarters.q1') },
        { value: 'Q2', label: t('screens:vatReturn.quarters.q2') },
        { value: 'Q3', label: t('screens:vatReturn.quarters.q3') },
        { value: 'Q4', label: t('screens:vatReturn.quarters.q4') },
    ], [t]);

    const [fiscalYears, setFiscalYears]               = useState<FiscalYear[]>([]);
    const [selectedFiscalYearId, setSelectedFiscalYearId] = useState('');
    const [selectedQuarter, setSelectedQuarter]       = useState('Q1');
    const [loadingYears, setLoadingYears]             = useState(false);
    const [calculating, setCalculating]               = useState(false);
    const [booking, setBooking]                       = useState(false);
    const [nilBooking, setNilBooking]                 = useState(false);
    const [unbooking, setUnbooking]                   = useState(false);
    const [showBookModal, setShowBookModal]           = useState(false);
    const [showNilModal, setShowNilModal]             = useState(false);
    const [showUnbookModal, setShowUnbookModal]       = useState(false);
    const [nilDeclarationError, setNilDeclarationError] = useState<string | null>(null);
    const [unbookError, setUnbookError]               = useState<string | null>(null);
    /** When true the calculator queries the all-time net balance of 1543 and folds it into the target field. */
    const [includePendingCorrections, setIncludePendingCorrections] = useState(false);
    /** Which form field (e.g. "1a") the correction should be added to. */
    const [correctionTargetField, setCorrectionTargetField] = useState<string>('1a');
    /** Tax rate percentage (e.g. 21 for 21 %). */
    const [correctionTaxRatePct, setCorrectionTaxRatePct] = useState<number>(21);

    // Single source of truth: the VatReturn entity as returned by the API.
    const [vatData, setVatData] = useState<VatReturn | null>(null);

    const fiscalYearProvider = useMemo(() => new FiscalYearRestDataProvider(), []);
    const vatReturnProvider  = useMemo(() => new VatReturnRestDataProvider(), []);

    // ── Load fiscal years on mount ────────────────────────────────────────────
    useEffect(() => {
        setLoadingYears(true);
        fiscalYearProvider.getAll()
            .then(years => {
                setFiscalYears(years);
                if (years.length > 0) setSelectedFiscalYearId(years[0].id);
            })
            .finally(() => setLoadingYears(false));
    }, [fiscalYearProvider]);

    // ── Auto-load saved record when period selection changes ──────────────────
    useEffect(() => {
        if (!selectedFiscalYearId) return;
        vatReturnProvider.getByPeriod(selectedFiscalYearId, selectedQuarter)
            .then(existing => setVatData(existing ?? null))
            .catch(() => setVatData(null));
    }, [selectedFiscalYearId, selectedQuarter, vatReturnProvider]);

    // ── Sync correction UI state from loaded vatData ──────────────────────────
    useEffect(() => {
        if (!vatData) return;
        if (vatData.correctionTargetField) {
            setCorrectionTargetField(vatData.correctionTargetField);
            setIncludePendingCorrections(true);
        }
        if (vatData.correctionTaxRate != null)
            setCorrectionTaxRatePct(Math.round(vatData.correctionTaxRate * 100));
    // Only re-sync when the record itself changes (not on every render).
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [vatData?.id]);

    // ── Auto-update default tax rate when target field changes ───────────────
    useEffect(() => {
        if (DEFAULT_TAX_RATE[correctionTargetField] != null)
            setCorrectionTaxRatePct(Math.round(DEFAULT_TAX_RATE[correctionTargetField] * 100));
    }, [correctionTargetField]);

    // ── Derived vatData with correction merged into the target field ──────────
    /**
     * For display purposes the correction VAT (1543) is folded into the chosen
     * form row rather than shown as a separate line item.
     *
     *  – VAT column of the target row  += correctionVat
     *  – Amount column of the target row += correctionVat / taxRate
     *  – VatPayable (5a)               += correctionVat  (keeps 5g consistent)
     *
     * The stored entity is unchanged; booking still clears 1543 via correctionVat.
     */
    const correctedVatData = useMemo((): VatReturn | null => {
        if (!vatData) return null;
        const { correctionVat, correctionTargetField: field, correctionTaxRate: rate } = vatData;
        if (!correctionVat || !field || !rate) return vatData; // nothing to merge

        const mapping = CORRECTION_FIELD_MAP[field];
        if (!mapping) return vatData; // unknown field — show as-is

        const amountAdj = rate > 0 ? Math.round((correctionVat / rate) * 100) / 100 : 0;
        return {
            ...vatData,
            [mapping.vatKey]:    ((vatData[mapping.vatKey]    as number | null) ?? 0) + correctionVat,
            [mapping.amountKey]: ((vatData[mapping.amountKey] as number | null) ?? 0) + amountAdj,
            vatPayable:          (vatData.vatPayable ?? 0) + correctionVat,
        };
    }, [vatData]);

    // ── Derived values (placed after correctedVatData so they can reference it) ─
    const isBooked          = vatData?.isBooked ?? false;
    const savedId           = vatData?.id ?? null;
    const total5g           = vatData?.totalVatPayable ?? 0;
    const hasExemptSupplies = (correctedVatData?.zeroRateSuppliesAmount ?? 0) > 0;

    /** True when a correction is currently merged into a field row. */
    const hasMergedCorrection = correctedVatData !== vatData && vatData?.correctionVat != null;
    /** Returns true if this field key is the target row for the merged correction. */
    const isCorrectionTarget = (key: string) =>
        hasMergedCorrection && vatData!.correctionTargetField === key;

    // ── Handlers ──────────────────────────────────────────────────────────────
    const handleCalculate = () => {
        if (!selectedFiscalYearId) return;
        setCalculating(true);
        const taxRateDecimal = correctionTaxRatePct > 0 ? correctionTaxRatePct / 100 : null;
        vatReturnProvider.calculate(
            selectedFiscalYearId,
            selectedQuarter,
            includePendingCorrections,
            includePendingCorrections ? correctionTargetField : null,
            includePendingCorrections ? taxRateDecimal : null,
        )
            .then(result => setVatData(result))
            .finally(() => setCalculating(false));
    };


    const handleBook = () => {
        if (!savedId) return;
        setBooking(true);
        vatReturnProvider.book(savedId)
            .then(() => {
                setVatData(prev => prev ? { ...prev, isBooked: true } : null);
                setShowBookModal(false);
            })
            .finally(() => setBooking(false));
    };

    const handleNilDeclaration = () => {
        if (!savedId) return;
        setNilBooking(true);
        setNilDeclarationError(null);
        vatReturnProvider.bookNilDeclaration(savedId)
            .then(() => {
                setVatData(prev => prev ? { ...prev, isBooked: true } : null);
                setShowNilModal(false);
            })
            .catch((err: Error) => {
                try { setNilDeclarationError(JSON.parse(err.message)?.message ?? err.message); }
                catch { setNilDeclarationError(err.message); }
            })
            .finally(() => setNilBooking(false));
    };

    const handleUnbook = () => {
        if (!savedId) return;
        setUnbooking(true);
        setUnbookError(null);
        vatReturnProvider.unbook(savedId)
            .then(() => {
                setVatData(prev => prev ? { ...prev, isBooked: false } : null);
                setShowUnbookModal(false);
            })
            .catch((err: Error) => {
                try { setUnbookError(JSON.parse(err.message)?.message ?? err.message); }
                catch { setUnbookError(err.message); }
            })
            .finally(() => setUnbooking(false));
    };

    // ── Display helpers ───────────────────────────────────────────────────────
    const fmt = (v: number) => v.toLocaleString('nl-NL', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    const val = (v: number | null | undefined) => (
        <span className="vat-return-value">{v == null ? '—' : fmt(v)}</span>
    );

    return (
        <div className="vat-return-page">
            <MainScreenHeader title={t('screens:vatReturn.title')}/>

            {/* ── Toolbar ── */}
            <Toolbar
                filters={[
                    {
                        key: 'fiscal-year',
                        type: 'custom',
                        label: t('common:fiscalYear'),
                        render: () => (
                            <select
                                className="toolbar__filter-select"
                                value={selectedFiscalYearId}
                                onChange={e => setSelectedFiscalYearId(e.target.value)}
                                disabled={loadingYears}
                            >
                                {loadingYears
                                    ? <option value="">{t('common:loading')}</option>
                                    : fiscalYears.map(fy => <option key={fy.id} value={fy.id}>{fy.name}</option>)
                                }
                            </select>
                        ),
                    } as ToolbarFilterConfig,
                    {
                        key: 'quarter',
                        type: 'select',
                        label: t('screens:vatReturn.quarter'),
                        value: selectedQuarter,
                        onChange: setSelectedQuarter,
                        options: quarters.map(q => ({ label: q.label, value: q.value })),
                    } as ToolbarFilterConfig,
                    {
                        key: 'include-corrections',
                        type: 'checkbox',
                        label: t('screens:vatReturn.includePendingCorrections'),
                        value: includePendingCorrections,
                        onChange: (v: boolean) => setIncludePendingCorrections(v),
                    } as ToolbarFilterConfig,
                    ...(includePendingCorrections ? [
                        {
                            key: 'correction-field',
                            type: 'select',
                            label: t('screens:vatReturn.correctionTargetField'),
                            value: correctionTargetField,
                            onChange: setCorrectionTargetField,
                            options: Object.keys(CORRECTION_FIELD_MAP).map(k => ({
                                label: t(`screens:vatReturn.correctionFieldOptions.${k}`),
                                value: k,
                            })),
                        } as ToolbarFilterConfig,
                        {
                            key: 'correction-rate',
                            type: 'custom',
                            render: () => (
                                <div className="toolbar__filter-item">
                                    <label className="toolbar__filter-label">
                                        {t('screens:vatReturn.correctionTaxRate')}
                                    </label>
                                    <input
                                        type="number"
                                        className="toolbar__filter-select vat-correction-rate-input"
                                        min={0}
                                        max={100}
                                        step={0.1}
                                        value={correctionTaxRatePct}
                                        onChange={e => setCorrectionTaxRatePct(parseFloat(e.target.value) || 0)}
                                    />
                                </div>
                            ),
                        } as ToolbarFilterConfig,
                    ] : []),
                ]}
                buttons={[
                    ...(!isBooked ? [
                        {
                            key: 'calculate',
                            icon: <FaCalculator />,
                            label: calculating ? t('screens:vatReturn.calculating') : t('screens:vatReturn.calculate'),
                            variant: 'secondary' as const,
                            onClick: handleCalculate,
                            disabled: calculating || !selectedFiscalYearId,
                        },
                        ...(savedId ? [
                            {
                                key: 'book',
                                icon: <FaBook />,
                                label: t('screens:vatReturn.book'),
                                variant: 'info' as const,
                                onClick: () => setShowBookModal(true),
                                disabled: booking,
                            },
                            ...(total5g <= 0 ? [{
                                key: 'nilDeclaration',
                                icon: <FaBan />,
                                label: nilBooking ? t('screens:vatReturn.nilBooking') : t('screens:vatReturn.nilDeclaration'),
                                variant: 'warning' as const,
                                onClick: () => setShowNilModal(true),
                                disabled: nilBooking,
                            }] : []),
                        ] : []),
                    ] : []),
                    ...(isBooked ? [{
                        key: 'undoBooking',
                        icon: <FaUndo />,
                        label: unbooking ? t('screens:vatReturn.undoing') : t('screens:vatReturn.undoBooking'),
                        variant: 'danger' as const,
                        onClick: () => setShowUnbookModal(true),
                        disabled: unbooking,
                        role: `${UserRoles.Admin},${UserRoles.Manager}`,
                    }] : []),
                ] as ToolbarButtonConfig[]}
            />

            <div className="vat-return-scroll">
                <div className="vat-return-container">
                    <table className="vat-return-table">
                        <thead>
                            <tr className="vat-col-header-row">
                                <th className="vat-col-code"></th>
                                <th className="vat-col-desc"></th>
                                <th className="vat-col-amount">{t('screens:vatReturn.columns.amountSubjectToVat')}</th>
                                <th className="vat-col-vat">{t('screens:vatReturn.columns.vatAmount')}</th>
                            </tr>
                        </thead>
                        <tbody>

                            {/* ── Section 1 ── */}
                            <tr className="vat-section-header">
                                <td className="vat-col-code"><strong>1</strong></td>
                                <td colSpan={3}><strong>{t('screens:vatReturn.sections.domesticSuppliesMade')}</strong></td>
                            </tr>
                            <tr className="vat-data-row">
                                <td className="vat-col-code">1a</td>
                                <td className="vat-col-desc">
                                    {t('screens:vatReturn.sections.taxed21')}
                                    {isCorrectionTarget('1a') && <CorrectionBadge vat={vatData!} fmt={fmt} />}
                                </td>
                                <td className="vat-col-amount">{val(correctedVatData?.highRateSuppliesAmount)}</td>
                                <td className="vat-col-vat">{val(correctedVatData?.highRateSuppliesVat)}</td>
                            </tr>
                            <tr className="vat-data-row">
                                <td className="vat-col-code">1b</td>
                                <td className="vat-col-desc">
                                    {t('screens:vatReturn.sections.taxed6')}
                                    {isCorrectionTarget('1b') && <CorrectionBadge vat={vatData!} fmt={fmt} />}
                                </td>
                                <td className="vat-col-amount">{val(correctedVatData?.lowRateSuppliesAmount)}</td>
                                <td className="vat-col-vat">{val(correctedVatData?.lowRateSuppliesVat)}</td>
                            </tr>
                            <tr className="vat-data-row">
                                <td className="vat-col-code">1c</td>
                                <td className="vat-col-desc">
                                    {t('screens:vatReturn.sections.taxedOther')}
                                    {isCorrectionTarget('1c') && <CorrectionBadge vat={vatData!} fmt={fmt} />}
                                </td>
                                <td className="vat-col-amount">{val(correctedVatData?.otherRateSuppliesAmount)}</td>
                                <td className="vat-col-vat">{val(correctedVatData?.otherRateSuppliesVat)}</td>
                            </tr>
                            <tr className="vat-data-row">
                                <td className="vat-col-code">1d</td>
                                <td className="vat-col-desc">
                                    {t('screens:vatReturn.sections.privateUse')}
                                    {isCorrectionTarget('1d') && <CorrectionBadge vat={vatData!} fmt={fmt} />}
                                </td>
                                <td className="vat-col-amount">{val(correctedVatData?.privateUseAmount)}</td>
                                <td className="vat-col-vat">{val(correctedVatData?.privateUseVat)}</td>
                            </tr>
                            <tr className="vat-data-row">
                                <td className="vat-col-code">1e</td>
                                <td className="vat-col-desc">{t('screens:vatReturn.sections.taxed0')}</td>
                                <td className="vat-col-amount">{val(correctedVatData?.zeroRateSuppliesAmount)}</td>
                                <td className="vat-col-vat vat-col-disabled"></td>
                            </tr>

                            {/* ── Section 2 ── */}
                            <tr className="vat-section-header">
                                <td className="vat-col-code"><strong>2</strong></td>
                                <td colSpan={3}><strong>{t('screens:vatReturn.sections.domesticSuppliesReceived')}</strong></td>
                            </tr>
                            <tr className="vat-data-row">
                                <td className="vat-col-code">2a</td>
                                <td className="vat-col-desc">
                                    {t('screens:vatReturn.sections.reverseCharged')}
                                    {isCorrectionTarget('2a') && <CorrectionBadge vat={vatData!} fmt={fmt} />}
                                </td>
                                <td className="vat-col-amount">{val(correctedVatData?.reverseChargeAmount)}</td>
                                <td className="vat-col-vat">{val(correctedVatData?.reverseChargeVat)}</td>
                            </tr>

                            {/* ── Section 3 ── */}
                            <tr className="vat-section-header">
                                <td className="vat-col-code"><strong>3</strong></td>
                                <td colSpan={3}><strong>{t('screens:vatReturn.sections.suppliesForeign')}</strong></td>
                            </tr>
                            <tr className="vat-data-row">
                                <td className="vat-col-code">3a</td>
                                <td className="vat-col-desc">{t('screens:vatReturn.sections.outsideEU')}</td>
                                <td className="vat-col-amount">{val(correctedVatData?.exportsOutsideEuAmount)}</td>
                                <td className="vat-col-vat vat-col-disabled"></td>
                            </tr>
                            <tr className="vat-data-row">
                                <td className="vat-col-code">3b</td>
                                <td className="vat-col-desc">{t('screens:vatReturn.sections.insideEU')}</td>
                                <td className="vat-col-amount">{val(correctedVatData?.exportsInsideEuAmount)}</td>
                                <td className="vat-col-vat vat-col-disabled"></td>
                            </tr>
                            <tr className="vat-data-row">
                                <td className="vat-col-code">3c</td>
                                <td className="vat-col-desc">{t('screens:vatReturn.sections.installationEU')}</td>
                                <td className="vat-col-amount">{val(correctedVatData?.installationDistanceSalesEuAmount)}</td>
                                <td className="vat-col-vat vat-col-disabled"></td>
                            </tr>

                            {/* ── Section 4 ── */}
                            <tr className="vat-section-header">
                                <td className="vat-col-code"><strong>4</strong></td>
                                <td colSpan={3}><strong>{t('screens:vatReturn.sections.suppliesFromForeign')}</strong></td>
                            </tr>
                            <tr className="vat-data-row">
                                <td className="vat-col-code">4a</td>
                                <td className="vat-col-desc">
                                    {t('screens:vatReturn.sections.fromOutsideEU')}
                                    {isCorrectionTarget('4a') && <CorrectionBadge vat={vatData!} fmt={fmt} />}
                                </td>
                                <td className="vat-col-amount">{val(correctedVatData?.importsOutsideEuAmount)}</td>
                                <td className="vat-col-vat">{val(correctedVatData?.importsOutsideEuVat)}</td>
                            </tr>
                            <tr className="vat-data-row">
                                <td className="vat-col-code">4b</td>
                                <td className="vat-col-desc">
                                    {t('screens:vatReturn.sections.acquisitionsEU')}
                                    {isCorrectionTarget('4b') && <CorrectionBadge vat={vatData!} fmt={fmt} />}
                                </td>
                                <td className="vat-col-amount">{val(correctedVatData?.intraEuAcquisitionsAmount)}</td>
                                <td className="vat-col-vat">{val(correctedVatData?.intraEuAcquisitionsVat)}</td>
                            </tr>

                            {/* ── Section 5 ── */}
                            <tr className="vat-section-header">
                                <td className="vat-col-code"><strong>5</strong></td>
                                <td colSpan={3}><strong>{t('screens:vatReturn.sections.vatCalculations')}</strong></td>
                            </tr>
                            <tr className="vat-data-row">
                                <td className="vat-col-code">5a</td>
                                <td className="vat-col-desc">{t('screens:vatReturn.sections.vatPayable')}</td>
                                <td className="vat-col-amount vat-col-disabled"></td>
                                <td className="vat-col-vat">{val(correctedVatData?.vatPayable)}</td>
                            </tr>
                            <tr className="vat-data-row">
                                <td className="vat-col-code">5b</td>
                                <td className="vat-col-desc">{t('screens:vatReturn.sections.inputTax')}</td>
                                <td className="vat-col-amount vat-col-disabled"></td>
                                <td className="vat-col-vat">{val(correctedVatData?.inputTax)}</td>
                            </tr>
                            <tr className="vat-data-row">
                                <td className="vat-col-code">5c</td>
                                <td className="vat-col-desc">{t('screens:vatReturn.sections.korReduction')}</td>
                                <td className="vat-col-amount vat-col-disabled"></td>
                                <td className="vat-col-vat">{val(correctedVatData?.korReduction)}</td>
                            </tr>

                            {/* ── Utility: estimated non-deductible input VAT ── */}
                            {hasExemptSupplies && (
                                <tr className="vat-utility-row">
                                    <td className="vat-col-code"></td>
                                    <td className="vat-col-desc">
                                        {t('screens:vatReturn.sections.exemptVatIndicator')}
                                        <div className="vat-total-note">{t('screens:vatReturn.sections.exemptVatIndicatorNote')}</div>
                                    </td>
                                    <td className="vat-col-amount vat-col-disabled"></td>
                                    <td className="vat-utility-value">{fmt((correctedVatData?.zeroRateSuppliesAmount ?? 0) * 0.21)}</td>
                                </tr>
                            )}

                            {/* ── 5g Total ── */}
                            <tr className="vat-total-row">
                                <td className="vat-col-code"><strong>5g</strong></td>
                                <td className="vat-col-desc">
                                    <strong>{t('screens:vatReturn.sections.totalPayableReceivable')}</strong>
                                    <div className="vat-total-note">{t('screens:vatReturn.sections.totalNote')}</div>
                                </td>
                                <td className="vat-col-amount"></td>
                                <td className="vat-col-vat vat-total-value">
                                    <TooltipCard
                                        placement="top"
                                        content={hasExemptSupplies ? (
                                            <div className="vat-tooltip-content">
                                                <div className="vat-tooltip-title">
                                                    <FaInfoCircle className="vat-tooltip-icon" />
                                                    {t('screens:vatReturn.tooltip.correctedForExempt')}
                                                </div>
                                                <div className="vat-tooltip-detail">
                                                    {t('screens:vatReturn.tooltip.correctedForExemptDetail')}
                                                </div>
                                            </div>
                                        ) : null}
                                    >
                                        <strong>{fmt(total5g)}</strong>
                                        {hasExemptSupplies && (
                                            <span className="vat-exempt-balloon" title={t('screens:vatReturn.tooltip.correctedForExempt')}>
                                                <FaInfoCircle />
                                            </span>
                                        )}
                                    </TooltipCard>
                                </td>
                            </tr>

                        </tbody>
                    </table>
                </div>
            </div>

            {showBookModal && (
                <div className="vat-modal-backdrop">
                    <div className="vat-modal-box">
                        <h5 className="vat-modal-title">{t('screens:vatReturn.confirmBooking')}</h5>
                        <p className="vat-modal-body" dangerouslySetInnerHTML={{ __html: t('screens:vatReturn.confirmBookingMessage', { quarter: selectedQuarter }) }} />
                        <div className="vat-modal-actions">
                            <button className="btn btn-secondary" type="button" onClick={() => setShowBookModal(false)} disabled={booking}>
                                {t('common:cancel')}
                            </button>
                            <button className="btn btn-primary" type="button" onClick={handleBook} disabled={booking}>
                                <FaBook style={{ marginRight: 7 }} />
                                {booking ? t('screens:vatReturn.booking') : t('screens:vatReturn.confirmBook')}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <PopupDialog
                open={showNilModal}
                title={t('screens:vatReturn.confirmNilDeclaration')}
                onClose={() => { setShowNilModal(false); setNilDeclarationError(null); }}
                actions={
                    <>
                        <button className="btn btn-secondary" type="button" onClick={() => { setShowNilModal(false); setNilDeclarationError(null); }} disabled={nilBooking}>
                            {t('common:cancel')}
                        </button>
                        <button className="btn btn-warning" type="button" onClick={handleNilDeclaration} disabled={nilBooking}>
                            <FaBan style={{ marginRight: 7 }} />
                            {nilBooking ? t('screens:vatReturn.nilBooking') : t('screens:vatReturn.confirmNilBook')}
                        </button>
                    </>
                }
            >
                <p dangerouslySetInnerHTML={{ __html: t('screens:vatReturn.confirmNilDeclarationMessage', { quarter: selectedQuarter }) }} />
                {nilDeclarationError && <ErrorMessage message={nilDeclarationError} />}
            </PopupDialog>

            <PopupDialog
                open={showUnbookModal}
                title={t('screens:vatReturn.confirmUndoBooking')}
                onClose={() => { setShowUnbookModal(false); setUnbookError(null); }}
                actions={
                    <>
                        <button className="btn btn-secondary" type="button" onClick={() => { setShowUnbookModal(false); setUnbookError(null); }} disabled={unbooking}>
                            {t('common:cancel')}
                        </button>
                        <button className="btn btn-danger" type="button" onClick={handleUnbook} disabled={unbooking}>
                            <FaUndo style={{ marginRight: 7 }} />
                            {unbooking ? t('screens:vatReturn.undoing') : t('screens:vatReturn.confirmUndo')}
                        </button>
                    </>
                }
            >
                <p dangerouslySetInnerHTML={{ __html: t('screens:vatReturn.confirmUndoBookingMessage', { quarter: selectedQuarter }) }} />
                {unbookError && <ErrorMessage message={unbookError} />}
            </PopupDialog>
        </div>
    );
};

export default VatReturn;
